import { useState, useEffect, useContext } from "react";
import { RectangleGroupIcon } from "@heroicons/react/24/outline";
import { AuthContext } from "../../../contexts/AuthContext";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { SideSheet } from "../../../components/sheet/sideSheet";
import { TemplateCard } from "./templateCard";

const TemplateSelection = ({ isOpen, onClose, onTemplateSelect }) => {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();

  const [search, setSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Fetch templates when the sheet opens
  useEffect(() => {
    if (isOpen && orgId) {
      setLoading(true);
      authFetch(
        `${process.env.REACT_APP_API_URL}/v1/shift-templates?org_id=${orgId}`
      )
        .then(async (res) => {
          if (!res.ok) {
            console.error("Failed to load templates:", res.status);
            return [];
          }
          return res.json();
        })
        .then((data) => {
          // The endpoint returns an array on success; guard against an error
          // body (or unexpected shape) so the list never crashes on .filter.
          setTemplates(Array.isArray(data) ? data : []);
        })
        .catch((error) => {
          console.error("Error loading templates:", error);
          setTemplates([]);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, orgId]);

  // Reset state when sheet opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setSelectedTemplate(null);
    }
  }, [isOpen]);

  const handleSave = () => {
    if (!selectedTemplate) {
      console.warn("No template selected, cannot save");
      return;
    }

    if (onTemplateSelect) {
      onTemplateSelect(selectedTemplate);
    }
  };

  // Filter templates by search term
  const filteredTemplates = templates.filter((template) => {
    if (search) {
      const searchLower = search.toLowerCase();
      return template.name && template.name.toLowerCase().includes(searchLower);
    }
    return true;
  });

  return (
    <SideSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Select template"
      footerText="Copy template"
      handleSave={handleSave}
      isForm={false}
      primaryButtonDisabled={!selectedTemplate}
      buttonLayout="auto"
    >
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Search input */}
        <div className="flex items-center mb-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search for template..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block justify-between gap-x-1.5 w-full rounded-md px-3 py-2.5 items-center text-sm leading-none font-normal text-gray-500 dark:text-gray-300 border-0 ring-1 ring-gray-100 dark:ring-gray-700 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-150 bg-gray-50 dark:bg-gray-800 focus:ring-2 focus:ring-gray-100 dark:focus:ring-gray-600 dark:placeholder-gray-500"
            />
          </div>
        </div>

        {/* Template cards list */}
        <div className="">
          {loading ? (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-md py-8">
              Loading templates...
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 bg-gray-50 dark:bg-gray-800 rounded-md">
              <RectangleGroupIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                {search ? "No templates found matching your search" : "No templates available"}
              </p>
            </div>
          ) : (
            filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                isSelected={selectedTemplate?.id === template.id}
                onClick={() => {
                  setSelectedTemplate(template);
                }}
              />
            ))
          )}
        </div>
      </div>
    </SideSheet>
  );
};

export { TemplateSelection };
